/**
 * EL INFORME DEL MES (documento «El informe del mes», 1-09-2026).
 *
 * «Lo que recibe cuando manda el reporte mensual. Las dos pantallas como tienen que quedar.»
 *
 * Diez bloques y en un orden que no es casual: primero dónde está y qué le vas a decir,
 * después lo que ha pasado con su cuerpo (peso, medidas, grasa, fotos) y al final lo que ha
 * hecho él (cumplimiento, su día tipo, sus alimentos, sus extras).
 *
 * DOS REGLAS DEL DOCUMENTO QUE MANDAN SOBRE TODO LO DEMÁS:
 *
 *   1. «El informe no le pide nada.» Lo único que se puede tocar son los selectores de las
 *      fotos y el botón de guardar el desayuno como plantilla. Ni un campo, ni una
 *      pregunta, ni un «sube tus fotos»: para eso está el reporte.
 *   2. Es UNA pantalla en dos momentos, no dos pantallas. Al enviar sale con el hueco del
 *      feedback en gris y con la hora; cuando le contestas, «el mismo informe, con tu
 *      bloque arriba. No es otro documento: es éste, completado».
 *
 * Un bloque sin datos NO SALE. El informe es lo único que el cliente lee del mes, y una
 * tarjeta vacía con un guion dentro se lee como que la app no sabe de él.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

const ORANGE = '#FF671F';
const VERDE = '#22C55E';
const ROJO = '#EF4444';

const COLOR = { verde: 'text-emerald-500', rojo: 'text-red-500', gris: 'text-foreground/30' };

/**
 * EL HUECO DE UN BLOQUE QUE NO TIENE DATOS (Jesús, punto 7 del 9-09).
 *
 * Los bloques se caían enteros cuando no había nada que pintar, y el cliente no veía ni el
 * hueco: recibía un informe más corto y daba por hecho que el informe era así. Y no es un
 * caso raro -- él lo midió: a 155 de 170 les llega sin la gráfica del peso.
 *
 * NO ROMPE SU REGLA 1 («el informe no le pide nada»), y por eso el texto EXPLICA y no pide:
 * «Este mes no registraste ninguno», nunca «sube tu peso». Es la frase que propuso él.
 */
const Hueco = ({ titulo, texto, testid }) => (
    <section className="rounded-2xl p-4 border border-dashed border-border/70 bg-card/40"
        data-testid={testid}>
        <p className="text-[13px] font-semibold text-muted-foreground">{titulo}</p>
        <p className="text-[13px] text-muted-foreground/80 mt-1">{texto}</p>
    </section>
);

const Tarjeta = ({ titulo, derecha, children, testid, tono }) => (
    <section
        className={`rounded-2xl p-4 space-y-3 border ${
            tono === 'naranja' ? '' : 'bg-card border-border'}`}
        style={tono === 'naranja'
            ? { borderColor: `${ORANGE}66`, backgroundColor: `${ORANGE}0D` } : undefined}
        data-testid={testid}>
        {(titulo || derecha) && (
            <div className="flex items-baseline justify-between gap-3">
                {/* En la tarjeta naranja el rótulo va en naranja, que es como lo pinta su
                    maqueta: el mismo bloque, apagado mientras es el hueco y encendido
                    cuando ya lleva su texto. */}
                <p className={`text-[11px] font-bold uppercase tracking-wider ${
                    tono === 'naranja' ? '' : 'text-muted-foreground'}`}
                    style={tono === 'naranja' ? { color: ORANGE } : undefined}>
                    {titulo}
                </p>
                {derecha && (
                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground text-right">
                        {derecha}
                    </p>
                )}
            </div>
        )}
        {children}
    </section>
);

/** Una fila de «etiqueta ......... valor», que es la forma de medio informe. */
const Fila = ({ que, valor, primera, testid }) => (
    <div className={`flex items-baseline justify-between gap-3 py-2 ${
        primera ? '' : 'border-t border-border'}`} data-testid={testid}>
        <span className="text-sm text-foreground/80">{que}</span>
        {/* El número no se parte nunca: «82,8 / kg» en dos líneas se lee como dos datos. */}
        <span className="text-sm font-bold text-foreground tabular-nums text-right whitespace-nowrap">
            {valor}
        </span>
    </div>
);

/**
 * La curva del peso: TODA su historia, con el mes del informe encendido encima.
 *
 * DECISIÓN DE FRANCISCO, 7-09-2026. La maqueta de Jesús rotula la curva «4 jun · 2 jul ·
 * 4 ago · 1 sep» dentro de un informe que va del 4 de agosto al 1 de septiembre: son tres
 * meses. Aquí se dibujaba solo el mes, y así 83 clientes de 185 no veían línea ninguna
 * -- se pesan una vez al mes y hacen falta dos puntos -- y otros 80 veían una recta entre
 * dos. Con la historia entera se quedan sin curva 19, los que solo se han pesado una vez.
 *
 * El mes no se pierde: va en verde encendido sobre la línea apagada de todo lo anterior, y
 * los círculos son sus pesajes más el primero de todos, el día que entró. Las cinco filas
 * de debajo y el reparto semanal siguen siendo del mes; esto es solo la línea.
 *
 * Los cuatro rótulos son las fechas que sitúan la línea, no los ejes de una gráfica.
 */
const CurvaDePeso = ({ puntos }) => {
    const ps = (puntos || [])
        .filter(p => p && p.fecha && Number.isFinite(Number(p.valor)))
        .map(p => ({
            ...p,
            valor: Number(p.valor),
            t: new Date(`${String(p.fecha).slice(0, 10)}T12:00:00`).getTime(),
        }))
        .filter(p => Number.isFinite(p.t));
    if (ps.length < 2) return null;

    const ancho = 300, alto = 64;
    const vs = ps.map(p => p.valor);
    const min = Math.min(...vs), max = Math.max(...vs);
    const rango = max - min || 1;
    // EL EJE DE ABAJO ES EL TIEMPO, no el número de pesajes. Repartiendo por índice, diez
    // pesajes de una semana ocupaban lo mismo que un año entero, y con la historia completa
    // eso deforma la línea hasta mentir.
    const t0 = ps[0].t, t1 = ps[ps.length - 1].t;
    const rangoT = (t1 - t0) || 1;
    const xy = (p) => [
        ((p.t - t0) / rangoT) * ancho,
        alto - 6 - ((p.valor - min) / rango) * (alto - 12),
    ];
    const linea = (arr) => arr.map(p => xy(p).join(',')).join(' ');

    // Los informes YA GUARDADOS no traen la marca del mes: entonces se pinta todo encendido,
    // que es exactamente como se veían antes.
    const hayMarca = ps.some(p => p.en_el_mes);
    const delMes = hayMarca ? ps.filter(p => p.en_el_mes) : ps;
    const conCirculo = hayMarca ? [...new Set([ps[0], ...delMes])] : ps;

    // Con más de cuatro meses a la vista, «4 jun» no distingue un año de otro. El año va
    // entero y no en dos cifras: «abr 26» se lee como el 26 de abril.
    const dias = rangoT / 86400000;
    const fecha = (f) => {
        const d = new Date(`${String(f).slice(0, 10)}T12:00:00`);
        if (isNaN(d)) return '';
        return d.toLocaleDateString('es-ES', dias > 120
            ? { month: 'short', year: 'numeric' }
            : { day: 'numeric', month: 'short' }).replace('.', '');
    };
    // Cuatro fechas repartidas EN EL TIEMPO, para que caigan donde cae su punto.
    const enElTiempo = (parte) => {
        const objetivo = t0 + rangoT * parte;
        return ps.reduce((mejor, p, i) =>
            Math.abs(p.t - objetivo) < Math.abs(ps[mejor].t - objetivo) ? i : mejor, 0);
    };
    const idx = [...new Set([0, enElTiempo(1 / 3), enElTiempo(2 / 3), ps.length - 1])];

    return (
        <div data-testid="informe-curva">
            <svg viewBox={`0 0 ${ancho} ${alto}`} className="w-full" style={{ height: alto }}
                preserveAspectRatio="none" aria-hidden="true">
                <polyline fill="none" stroke={VERDE} strokeOpacity={hayMarca ? 0.35 : 1}
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke" points={linea(ps)} />
                {/* El mes, más gordo que el resto: en un cliente con un año de historia
                    ocupa una octava parte del ancho, y si va del mismo grosor se pierde. */}
                {hayMarca && delMes.length > 1 && (
                    <polyline fill="none" stroke={VERDE} strokeWidth="3" strokeLinecap="round"
                        strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                        points={linea(delMes)} />
                )}
                {conCirculo.map((p, i) => {
                    const [x, y] = xy(p);
                    return (
                        <circle key={`${p.fecha}-${i}`} cx={x} cy={y} r="3" fill="none"
                            stroke={VERDE} strokeWidth="2" vectorEffect="non-scaling-stroke" />
                    );
                })}
            </svg>
            <div className="flex justify-between mt-1">
                {idx.map(i => (
                    <span key={i} className="text-[13px] text-muted-foreground">
                        {fecha(ps[i].fecha)}
                    </span>
                ))}
            </div>
        </div>
    );
};

/** 6 · Tus fotos. Dos, y las elige él: es uno de los dos sitios donde puede tocar algo. */
const TusFotos = ({ api, token, tipo }) => {
    const [fotos, setFotos] = useState(null);
    const [pose, setPose] = useState('frente');
    const [izq, setIzq] = useState('');
    const [der, setDer] = useState('');
    const [urls, setUrls] = useState({});

    useEffect(() => {
        api.get('/reports/photos')
            .then(r => setFotos(r.data?.photos || []))
            .catch(() => setFotos([]));
    }, [api]);

    const dePose = (fotos || []).filter(f => (f.pose || 'frente') === pose && f.taken_at)
        .sort((a, b) => String(a.taken_at).localeCompare(String(b.taken_at)));

    // Al cambiar de pose se eligen solas la primera y la última, que es la comparación que
    // más dice. A partir de ahí manda él.
    useEffect(() => {
        if (!dePose.length) { setIzq(''); setDer(''); return; }
        setIzq(dePose[0].id);
        setDer(dePose[dePose.length - 1].id);
    }, [pose, fotos]);   // eslint-disable-line react-hooks/exhaustive-deps

    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const traer = useCallback((id) => {
        if (!id || urls[id]) return;
        // Encogida, como las demás (12-09). Aquí un poco mayor que en la lista: la del
        // informe se mira despacio y es la que se compara con la del mes anterior.
        fetch(`${base}/api/reports/photos/${id}?w=1440`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => (r.ok ? r.blob() : Promise.reject(new Error('no'))))
            .then(b => setUrls(u => ({ ...u, [id]: URL.createObjectURL(b) })))
            .catch(() => {});
    }, [base, token, urls]);
    useEffect(() => { traer(izq); traer(der); }, [izq, der, traer]);

    if (fotos === null) return null;     // todavia cargando: ni bloque ni hueco
    // SIN FOTOS, EL HUECO CON SU PORQUE (Jesús, punto 7 del 9-09). Antes el bloque
    // desaparecía entero y el cliente ni sabía que existía. El texto explica, no pide, que
    // es lo que manda su regla 1: «el informe no le pide nada».
    if (!fotos.length) {
        return (
            <Hueco testid="informe-fotos-vacio" titulo="Tus fotos"
                texto={delPeriodo(tipo).el
                    ? `Aquí irían tus fotos de ${delPeriodo(tipo).el}. `
                      + `No subiste ninguna ${delPeriodo(tipo).este}.`
                    : 'Aquí irían tus fotos. No subiste ninguna.'} />
        );
    }

    const cuando = (f) => {
        const d = new Date(`${String(f.taken_at).slice(0, 10)}T12:00:00`);
        return isNaN(d) ? f.taken_at
            : d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    };

    /*
     * EL «ANTES» NO PUEDE IR DESPUÉS DEL «DESPUÉS» (Jesús, punto 38 de «La app, paso a
     * paso»: «en orden, y que el de la izquierda no pueda ser posterior al de la derecha»).
     *
     * Los dos desplegables listaban las mismas fechas enteras, así que se podía poner a la
     * izquierda una foto posterior a la de la derecha: el informe enseñaba la evolución del
     * revés y el cliente leía que había ido para atrás cuando había ido para adelante. Cada
     * lado se filtra ahora con el otro: a la izquierda solo lo anterior o igual a la
     * derecha, a la derecha solo lo posterior o igual a la izquierda. Con eso el par nunca
     * puede quedar cruzado, ni al abrir ni tocando.
     *
     * Se compara por DÍA y no por la posición en la lista porque de un mismo día puede
     * haber más de una foto de la misma pose (subió dos, o vino una de Calma con la de la
     * app), y esas dos tienen que seguir siendo elegibles en los dos lados.
     *
     * Y con el id buscado como TEXTO: el que elige el cliente llega del `<select>` siempre
     * como cadena, y el de la lista es el que venga del servidor. Comparados a secas, el
     * día del otro lado saldría vacío y los dos desplegables volverían a salir enteros:
     * o sea, este arreglo apagado y sin que se note.
     */
    const diaDe = (f) => String(f?.taken_at || '').slice(0, 10);
    const laFotoDe = (id) => dePose.find(f => String(f.id) === String(id)) || null;
    const diaIzq = diaDe(laFotoDe(izq));
    const diaDer = diaDe(laFotoDe(der));
    // Mientras el lado contrario no tenga una foto reconocible (el instante entre cambiar
    // de pose y que se elijan solas la primera y la última) no se recorta nada: recortar
    // por un dato que aún no está dejaría fuera fotos que sí valen.
    const hastaLaDerecha = diaDer ? dePose.filter(f => diaDe(f) <= diaDer) : dePose;
    const desdeLaIzquierda = diaIzq ? dePose.filter(f => diaDe(f) >= diaIzq) : dePose;

    return (
        <Tarjeta titulo="Tus fotos" testid="informe-fotos">
            <div className="flex gap-2">
                {/* LA POSE SE GUARDA EN SINGULAR, «espalda». El botón decía «espaldas» y por
                    eso salía apagado SIEMPRE: no hay ni habrá una sola foto con esa pose --
                    `routes/checkins.py` solo acepta frente, espalda y perfil, y lo que no
                    esté en esa lista lo guarda sin pose. El rótulo sí es «Espaldas», que es
                    como se dice; lo que tenía que casar es la clave. */}
                {[['frente', 'Frente'], ['espalda', 'Espaldas'], ['perfil', 'Perfil']].map(([v, l]) => {
                    const hay = (fotos || []).some(f => (f.pose || 'frente') === v);
                    const activo = pose === v;
                    return (
                        <button key={v} type="button" disabled={!hay}
                            onClick={() => setPose(v)} data-testid={`informe-pose-${v}`}
                            className={`px-3 py-1.5 rounded-full text-[13px] font-bold transition-colors disabled:opacity-30 ${
                                activo ? 'text-white' : 'bg-muted text-foreground/60'}`}
                            style={activo ? { backgroundColor: ORANGE } : undefined}>
                            {l}
                        </button>
                    );
                })}
            </div>

            {dePose.length ? (
                <>
                    <div className="grid grid-cols-2 gap-2">
                        {[[izq, setIzq, 'izq', hastaLaDerecha], [der, setDer, 'der', desdeLaIzquierda]]
                            .map(([valor, poner, lado, elegibles]) => (
                            <select key={lado} value={valor} onChange={(e) => poner(e.target.value)}
                                data-testid={`informe-foto-${lado}`}
                                className="bg-muted border border-border rounded-xl px-3 py-2 text-[13px] text-foreground outline-none focus:border-[#FF671F]">
                                {elegibles.map(f => (
                                    <option key={f.id} value={f.id}>{cuando(f)}</option>
                                ))}
                            </select>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {[izq, der].map((id, i) => (
                            <div key={i} className="aspect-[3/4] rounded-xl overflow-hidden bg-muted">
                                {urls[id] && <img src={urls[id]} alt="" className="w-full h-full object-cover" />}
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <p className="text-sm text-muted-foreground">
                    De esta pose todavía no hay ninguna.
                </p>
            )}
        </Tarjeta>
    );
};

/**
 * «GUÁRDAMELA COMO PLANTILLA» (bloque 8 de su documento).
 *
 * Guarda la combinación que él ya reconoce como su desayuno -- los alimentos del día tipo,
 * con las cantidades que enseña la fila, que son la MEDIANA del mes -- como una favorita de
 * COMIDA, que es la que luego se pone de un toque en cualquier comida desde Nutrición.
 *
 * El nombre se pone solo y con la fecha detrás: pedirle que escriba uno sería pedirle algo,
 * y el informe no le pide nada. Si ya tiene una con ese nombre, el servidor no protesta
 * (eso solo lo mira la pantalla de favoritas del día), así que la fecha las distingue.
 */
const ComoPlantilla = ({ api, fila }) => {
    const [estado, setEstado] = useState('');       // '' | 'guardando' | 'hecho'
    const guardar = async () => {
        if (estado) return;
        setEstado('guardando');
        try {
            await api.post('/diets/favorites', {
                name: `${fila.nombre} de siempre · ${new Date().toLocaleDateString('es-ES',
                    { day: 'numeric', month: 'long' })}`,
                ambito: 'comida',
                comida: fila.clave,
                alimentos: (fila.items || [])
                    .filter((x) => x.alimento_id != null)
                    .map((x) => ({ alimento_id: x.alimento_id, nombre: x.nombre,
                                   cantidad_g: x.gramos })),
            });
            setEstado('hecho');
        } catch (e) {
            console.error('[informe] no se pudo guardar la plantilla', e);
            setEstado('');
            toast.error('No hemos podido guardarla. Inténtalo de nuevo.');
        }
    };
    // Ya guardada, el botón se queda diciendo dónde está: guardarla dos veces no aporta
    // nada y dejar el botón igual invita a hacerlo.
    if (estado === 'hecho') {
        return (
            <span className="mt-2 block text-[13px]" style={{ color: VERDE }}
                data-testid="informe-plantilla-hecha">
                Guardada · La tienes en tus comidas favoritas
            </span>
        );
    }
    return (
        <button type="button" onClick={guardar} disabled={estado === 'guardando'}
            data-testid="informe-guardar-plantilla"
            className="mt-2 rounded-full border px-3 py-1.5 text-[13px] font-bold transition-colors
                       disabled:opacity-60"
            style={{ borderColor: ORANGE, color: ORANGE }}>
            {estado === 'guardando' ? 'Guardando...' : 'Guárdamela como plantilla'}
        </button>
    );
};

/**
 * EL INFORME SE LLAMA COMO EL REPORTE QUE LO GENERA (Francisco, 11-09).
 *
 * «El informe de un reporte quincenal se llama "informe mensual".»
 *
 * El título estaba escrito a mano porque cuando se hizo esta pantalla el informe solo lo
 * generaba el mensual. Ahora lo generan los tres, y el tipo viaja dentro del informe
 * (`core/informe_mensual.montar_informe`). Los informes YA GUARDADOS no lo traen -- se
 * congelan al enviar el reporte --, y entonces se dice «Tu informe» a secas: no se adivina.
 */
const TITULO = {
    quincenal: 'Tu informe quincenal',
    mensual: 'Tu informe mensual',
    semanal: 'Tu informe semanal',
};

/**
 * Y EL PERIODO, POR SU NOMBRE (Francisco, 13-09: «que diga quincena en vez de mes cuando
 * sea quincenal»). El título ya se llamaba por su tipo, pero por dentro el informe seguía
 * hablando de mes en todas partes: «Empezaste el mes en», «tus fotos del mes», «las veces
 * que las has puesto este mes». En un quincenal de siete días eso cuenta otra cosa.
 *
 * Sin tipo -- los informes ya guardados no lo traen -- se quita la coletilla y la frase se
 * queda sin periodo: «Empezaste en», «Aquí irían tus fotos». Se lee bien y no miente.
 * El rótulo de la esquina («−2,8 kg esta quincena») lo escribe el servidor con las mismas
 * palabras, en `core/informe_del_mes.palabras_del_periodo`.
 */
const PERIODO = {
    quincenal: { el: 'la quincena', este: 'esta quincena', enEste: 'esta quincena' },
    mensual: { el: 'el mes', este: 'este mes', enEste: 'este mes' },
    semanal: { el: 'la semana', este: 'esta semana', enEste: 'esta semana' },
};
const delPeriodo = (tipo) => PERIODO[tipo] || { el: '', este: '', enEste: '' };

const InformeDelMes = ({ informe, api, token }) => {
    const b = informe?.bloques;
    if (!b) return null;

    const { periodo, donde_estas: donde, feedback, peso, medidas, grasa, hecho,
            dia_tipo: dia, preferencias, extras } = b;

    return (
        <div className="space-y-3" data-testid="informe-del-mes">
            {/* ── CABECERA ── */}
            <div>
                {periodo?.label && (
                    <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                        {periodo.label}
                    </p>
                )}
                <h2 className="text-2xl font-bold text-foreground uppercase"
                    style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                    {TITULO[informe?.tipo] || 'Tu informe'}
                </h2>
            </div>

            {/* ── 1 · DÓNDE ESTÁS ── */}
            {(donde?.objetivo_label || donde?.ciclo_label) && (
                <Tarjeta testid="informe-donde-estas">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                Tu objetivo
                            </p>
                            <p className="text-base font-bold text-foreground">
                                {donde.objetivo_label || '—'}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                Tu ciclo
                            </p>
                            <p className="text-base font-bold" style={{ color: ORANGE }}>
                                {donde.ciclo_label || '—'}
                            </p>
                        </div>
                    </div>
                </Tarjeta>
            )}

            {/* ── 2 · TU FEEDBACK Y TU PROGRAMA NUEVO ── */}
            {/* CON SU RÓTULO, que su maqueta lo pinta en LOS DOS momentos -- en gris cuando
                es el hueco y en naranja cuando ya está escrito -- y aquí no salía en ninguno.
                Sin él, el aviso de «estamos revisando» se lee como una nota suelta debajo del
                ciclo y no como el bloque 2, que es lo que luego se completa. */}
            {feedback?.pendiente ? (
                <Tarjeta titulo="Tu feedback y tu programa nuevo"
                    testid="informe-feedback-pendiente">
                    <p className="text-sm text-muted-foreground">{feedback.aviso}</p>
                </Tarjeta>
            ) : feedback?.texto ? (
                <Tarjeta titulo="Tu feedback y tu programa nuevo" tono="naranja"
                    testid="informe-feedback">
                    <p className="text-base text-foreground leading-snug">{feedback.texto}</p>
                    <div className="flex items-center gap-2 pt-1">
                        {feedback.iniciales && (
                            <span className="w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold text-white"
                                style={{ backgroundColor: ORANGE }}>
                                {feedback.iniciales}
                            </span>
                        )}
                        <span className="text-[13px] text-muted-foreground">
                            {feedback.firma}{feedback.fecha_label ? ` · ${feedback.fecha_label}` : ''}
                        </span>
                    </div>
                </Tarjeta>
            ) : null}

            {/* ── 3 · TU PESO ── */}
            {!peso?.hay && (
                <Hueco testid="informe-peso-vacio" titulo="Tu peso"
                    texto="Aquí iría la evolución de tu peso. Este mes no registraste ninguno." />
            )}
            {peso?.hay && (
                <Tarjeta titulo="Tu peso" derecha={peso.titulo_cambio} testid="informe-peso">
                    {/* `curva` es la historia entera con el mes marcado; `serie` (solo el
                        mes) es lo que traen los informes guardados de antes. */}
                    <CurvaDePeso puntos={peso.curva || peso.serie} />
                    <div className="-my-2">
                        <Fila primera valor={peso.empezaste_label}
                            que={delPeriodo(informe?.tipo).el
                                ? `Empezaste ${delPeriodo(informe?.tipo).el} en` : 'Empezaste en'} />
                        <Fila que="Lo acabas en" valor={peso.acabas_label} />
                        {/* «DESDE TU ÚLTIMO REPORTE: 0 KG» CUANDO EL ANTERIOR ERA UN KILO
                            MÁS (Francisco, 11-09). Aquí salía `cambio_label`, que es el
                            cambio DENTRO del mes: al que se pesó una sola vez, el primero
                            y el último son el mismo pesaje y la fila cantaba un cero.
                            `desde_el_reporte_label` cuenta desde donde estaba en el
                            reporte anterior, que es lo que dice la fila. Los informes
                            guardados de antes no lo traen y se quedan con el de siempre. */}
                        <Fila que="Desde tu último reporte"
                            valor={peso.desde_el_reporte_label || peso.cambio_label} />
                        {peso.al_empezar_label && (
                            <Fila que="Cuando empezaste pesabas" valor={peso.al_empezar_label} />
                        )}
                        {peso.desde_el_principio_label && (
                            <Fila que="Desde que empezaste" valor={peso.desde_el_principio_label} />
                        )}
                    </div>
                    {(peso.por_semana || []).length > 0 && (
                        <div className="pt-2 border-t border-border">
                            {/* «BAJANDO» SOLO SI BAJA. La frase era fija y a un cliente de
                                volumen le decía «lo que has ido bajando» encima de un
                                «+2,1 kg este mes»: el mismo bloque contando dos cosas
                                contrarias. El reparto es del cambio, sea en la dirección
                                que sea. */}
                            {/* EN KILOS CUANDO EL PORCENTAJE NO DICE NADA. El servidor manda
                                `pct: null` en los meses que suben y bajan y acaban casi
                                igual: ahí la parte se calcula sobre un total diminuto y se
                                dispara («Semana 2 · 390 %»). Los kilos de cada semana
                                contestan la misma pregunta y son verdad siempre. */}
                            <p className="text-[13px] text-muted-foreground mb-1">
                                {peso.por_semana[0]?.pct == null
                                    ? 'Lo que se movió tu peso cada semana. Este mes subió y bajó, así que el reparto en porcentaje no diría nada.'
                                    : `Porcentaje del peso total que has ido ${peso.cambio < 0 ? 'bajando' : 'subiendo'} por semana.`}
                            </p>
                            <div className="-my-2">
                                {peso.por_semana.map((s, i) => (
                                    // Con el menos de verdad, no un guion: es la misma regla
                                    // que `_con_signo` en el servidor, y en esta tarjeta se
                                    // leían los dos signos a la vez («−2,8 kg» y «-290 %»).
                                    <Fila key={s.semana} primera={i === 0}
                                        que={`Semana ${s.semana}`}
                                        valor={s.pct == null
                                            ? `${s.kg < 0 ? '−' : s.kg > 0 ? '+' : ''}${Math.abs(s.kg).toFixed(1).replace('.', ',')} kg`
                                            : `${s.pct < 0 ? '−' : ''}${Math.abs(s.pct)} %`} />
                                ))}
                            </div>
                        </div>
                    )}
                </Tarjeta>
            )}

            {/* ── 4 · TUS MEDIDAS ── */}
            {!medidas?.hay && (
                <Hueco testid="informe-medidas-vacio" titulo="Tus medidas"
                    texto="Aquí irían tus diez medidas. Este mes no las tomaste." />
            )}
            {medidas?.hay && (
                <Tarjeta titulo="Tus medidas" testid="informe-medidas">
                    <div className="grid grid-cols-[1fr_3.5rem_3.5rem] gap-2 items-baseline">
                        <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                            Diez tomas
                        </span>
                        <span className="text-[13px] text-muted-foreground text-right">Mes ant.</span>
                        <span className="text-[13px] text-muted-foreground text-right">1ª toma</span>
                        {medidas.filas.map(f => (
                            <React.Fragment key={f.clave}>
                                <span className="text-sm text-foreground/80 border-t border-border pt-2">
                                    {f.etiqueta}
                                </span>
                                {['mes', 'primera'].map(cual => (
                                    <span key={cual}
                                        className={`text-base font-bold tabular-nums text-right border-t border-border pt-2 ${
                                            f[cual] ? COLOR[f[cual].color] : 'text-foreground/20'}`}>
                                        {f[cual] ? f[cual].label : '—'}
                                    </span>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 5 · TU PORCENTAJE DE GRASA ── */}
            <Tarjeta titulo="Tu porcentaje de grasa" derecha={grasa?.cuando}
                testid="informe-grasa">
                <p className="text-sm text-foreground/80">{grasa?.explicacion}</p>
                {grasa?.hay && <p className="text-sm text-foreground/80">{grasa.ultima}</p>}
            </Tarjeta>

            {/* ── 6 · TUS FOTOS ── */}
            {api && <TusFotos api={api} token={token} tipo={informe?.tipo} />}

            {/* ── 7 · LO QUE HAS HECHO ── */}
            {hecho?.hay && (
                <Tarjeta titulo="Lo que has hecho" testid="informe-hecho">
                    <div className="-my-1">
                        {hecho.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-hecho-${f.clave}`}
                                className={`grid grid-cols-2 gap-3 py-2.5 ${
                                    i ? 'border-t border-border' : ''}`}>
                                <span className="flex items-baseline justify-between gap-2">
                                    <span className="text-sm text-foreground/80">{f.etiqueta}</span>
                                    <span className="text-sm font-bold text-foreground tabular-nums">{f.valor}</span>
                                </span>
                                {f.valor2 != null && (
                                    <span className="flex items-baseline justify-between gap-2">
                                        <span className="text-sm text-foreground/80">{f.etiqueta2}</span>
                                        <span className="text-sm font-bold text-foreground tabular-nums">{f.valor2}</span>
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 8 · TU DÍA TIPO ── */}
            {dia?.hay && (
                <Tarjeta titulo="Tu día tipo" testid="informe-dia-tipo">
                    <p className="text-[13px] text-muted-foreground -mt-1">
                        La combinación que más repites en cada comida, y cuántos días.
                    </p>
                    <div className="-my-1">
                        {dia.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-dia-${f.clave}`}
                                className={`grid grid-cols-[4.5rem_1fr] gap-3 py-3 ${
                                    i ? 'border-t border-border' : ''}`}>
                                <span className="text-[13px] font-bold" style={{ color: ORANGE }}>
                                    {f.momento}
                                </span>
                                <span>
                                    <span className="block text-sm font-bold text-foreground">{f.nombre}</span>
                                    <span className="block text-[13px] text-muted-foreground">{f.texto}</span>
                                    <span className="block text-base text-foreground/70 mt-0.5">{f.cuantos}</span>
                                    {/* EL ÚNICO BOTÓN DEL INFORME, ADEMÁS DE LOS SELECTORES DE
                                        LAS FOTOS. Su documento lo dice dos veces: la maqueta lo
                                        dibuja bajo el desayuno y el pie remata «lo único que
                                        puede tocar son los selectores de las fotos y el botón de
                                        guardar SU DESAYUNO como plantilla». Por eso va en la
                                        primera comida del día y no en todas: ponerlo en las seis
                                        convertiría el informe en una pantalla de botones, que es
                                        justo lo que él no quiere.
                                        Solo cuando esa comida es una de verdad: si «cambia casi
                                        cada día» no hay plantilla que guardar. */}
                                    {i === 0 && !f.varia && (f.items || []).some(x => x.alimento_id != null) && (
                                        <ComoPlantilla api={api} fila={f} />
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 9 · PREFERENCIAS CONCRETAS DE ALIMENTOS ── */}
            {preferencias?.hay && (
                <Tarjeta titulo="Preferencias concretas de alimentos" testid="informe-preferencias">
                    <p className="text-[13px] text-muted-foreground -mt-1">
                        Tus fuentes de proteína, hidratos y grasa preferidas, y{' '}
                        <b className="text-foreground">las veces que las has puesto</b>
                        {delPeriodo(informe?.tipo).enEste ? ` ${delPeriodo(informe?.tipo).enEste}.` : '.'}
                    </p>
                    {/* EN SINGULAR, COMO TODA LA APP (Francisco, 11-09: «"Proteínas /
                        Hidratos / Grasas" en plural... En toda la app es singular»). El
                        arreglo del 11-09 pasó por Mis macros, el comparador, el chat y la
                        calculadora, y este rótulo del informe se quedó en «Grasas». La
                        clave del dato (`grasas`) no se toca: es la que manda el servidor. */}
                    {[['proteina', 'Proteína'], ['hidratos', 'Hidratos'], ['grasas', 'Grasa']].map(([k, l]) => (
                        (preferencias[k] || []).length > 0 && (
                            <div key={k} data-testid={`informe-preferencias-${k}`}>
                                <p className="text-[13px] font-bold mb-1" style={{ color: ORANGE }}>{l}</p>
                                <div className="-my-1">
                                    {preferencias[k].map((a, i) => (
                                        <Fila key={a.nombre} primera={i === 0}
                                            que={a.nombre} valor={a.label} />
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </Tarjeta>
            )}

            {/* ── 10 · EXTRAS REGISTRADOS ── */}
            {!extras?.hay && (
                <Hueco testid="informe-extras-vacio" titulo="Tus extras"
                    texto="Aquí iría lo que comiste fuera de la dieta. Este mes no apuntaste nada." />
            )}
            {extras?.hay && (
                <Tarjeta titulo="Extras registrados" testid="informe-extras">
                    <p className="text-[13px] text-muted-foreground -mt-1">{extras.titulo}</p>
                    <div className="-my-1">
                        {extras.lista.map((e, i) => (
                            <div key={`${e.fecha}-${i}`}
                                className={`flex items-baseline gap-3 py-2 ${
                                    i ? 'border-t border-border' : ''}`}>
                                <span className="text-[13px] text-muted-foreground w-14 shrink-0">
                                    {e.dia_label}
                                </span>
                                <span className="text-sm text-foreground">{e.texto}</span>
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}
        </div>
    );
};

export default InformeDelMes;
